import {
  Button,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  useMergeRefs,
  useSafeLayoutEffect,
} from '@chakra-ui/react';
import { JSONValue } from '@twilio/conversations';
import { BaseEmoji } from 'emoji-mart';
import { FC, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ReplyIcon from '@/assets/icons/ic-reply.svg';
import SendIcon from '@/assets/icons/ic-send-message.svg';

import { ChatEmojiPicker } from '@/components/ui/shared/conversation/Chat/ChatMessageInput.elements';
import { IConversationMessage } from '@/utils/conversation/types';

import { ReplyButton } from '../NestedChatMessage.elements';
import { useMessageReply } from '../NestedChatMessage.hooks';

interface INestedChatMessageReplyProps {
  message: IConversationMessage;
  messageAttributes: JSONValue;
}

export const NestedChatMessageReply: FC<INestedChatMessageReplyProps> = ({ message, messageAttributes }) => {
  const { t } = useTranslation();
  const { onReply, isLoading, onToggleReply, storedInput, storeInput, isReplyVisible, inputProps } = useMessageReply(
    message,
    messageAttributes
  );
  const [replyValue, setReplyValue] = useState(storedInput);

  const inputRef = useRef<HTMLInputElement>(null);

  useSafeLayoutEffect(() => {
    return () => {
      const value = inputRef.current?.value;

      if (isReplyVisible) {
        storeInput(value);
      }
    };
  }, [isReplyVisible, storeInput]);

  const onEmojiSelect = (emoji: BaseEmoji) => {
    setReplyValue(`${replyValue}${emoji.native}`);
    inputRef.current.focus();
  };

  const onToggle = () => {
    setReplyValue('');
    onToggleReply();
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onReply(replyValue).then(() => {
        setReplyValue('');
      });
    }
  };

  const handleOnReplyClick = () => {
    onReply(replyValue).then(() => {
      setReplyValue('');
    });
  };

  const refs = useMergeRefs(inputRef, inputProps.ref);

  return (
    <Fragment>
      <ReplyButton aria-label="reply" icon={<ReplyIcon width={24} height={24} />} onClick={onToggle} />
      {isReplyVisible ? (
        <InputGroup size="md" pb={2}>
          <Input
            {...inputProps}
            ref={refs}
            pr="5rem"
            disabled={isLoading}
            onKeyDown={onKeyDown}
            value={replyValue}
            placeholder={t('conversation.sendMessageReplyPlaceholder')}
            onChange={(event) => setReplyValue(event.target.value)}
          />
          <InputRightElement width="5rem">
            <ChatEmojiPicker mr={3} onEmojiSelect={onEmojiSelect} />
            <Button
              variant="link"
              colorScheme="violet"
              aria-label="Send message"
              alignSelf="center"
              maxW="min-content"
              minH={8}
              disabled={!replyValue}
              onClick={handleOnReplyClick}
              isLoading={isLoading}
            >
              <Icon w="24px" h="24px" ml={1} as={SendIcon} />
            </Button>
          </InputRightElement>
        </InputGroup>
      ) : null}
    </Fragment>
  );
};
